import { createQueryKeys } from '@lukemorales/query-key-factory'
import apiClient from '../service/api'
import {
  CancellationRefundQuote,
  CompleteRefundRequest,
  CompleteRefundResponse,
  CreditPackRefundQuote,
  InvoiceRefund,
  InvoiceRefundView,
  PaymentOrderRefundView,
  ReceiveRollbackRefundRequest,
  ReceiveRollbackRefundResponse,
  RecordRefundRequest,
  RecordRefundResponse,
  RefundLedgerRow,
  RollbackSettlementQuote,
  RollbackStayQuote,
} from '../types/refund'

/**
 * 환불 어드민 API (aivatar #488 · aipark-front #266)
 *
 * BE 기준: `feature/payment-phase1` (5f7a0b0c). 전부 `ROLE_admin` 단일 권한이다 —
 * 명세의 "어드민 접근 권한자 / 환불 처리 담당자" 2단계는 BE 에 없어 화면에서 분기하지 않는다.
 *
 * 흐름은 **접수 → 처리 완료** 2단계다. 접수 행이 먼저 생기고(아직 돈이 나가지 않았다),
 * 담당자가 결제대행사 화면에서 취소를 실행한 뒤 처리 완료를 확정한다. 서버가 금액 산정과
 * PortOne 취소를 대신 실행하지는 않는다.
 *
 * ⚠️ 산정 3종(`getCancellationRefundQuote`·`getRollbackStayQuote`·`getRollbackSettlementQuote`)은
 * **400 이 정상 응답**이다. 반드시 `utils/quoteError` 의 `quoteOrReason` 으로 감싸 쓴다.
 *
 * ⚠️ 반대로 확정·상태 전이 4종의 400 은 **진짜 거절**이고, 사유는 `BadRequestException` 메시지에
 * 들어 있다. 다만 `AdminInvoiceRefundController` 에는 다른 컨트롤러들이 가진
 * `@ExceptionHandler(BadRequestException.class)` 가 없어 본문에 사유가 비어 올 수 있다 —
 * `utils/apiError` 의 `apiErrorMessage` 가 그 경우를 `null` 로 구분해서 낸다.
 */

/* ────────────────────────────── 산정 (미리보기·부수효과 없음) ────────────────────────────── */

/**
 * 구독취소 환불 산정 — 월 구독취소·연 중도해지.
 *
 * 워크스페이스가 아니라 **구독 id** 로 지목한다. 한 워크스페이스에 구독 행이 여럿이라
 * (현행·해지 예약·전환 예약·과거) 서버가 골라주는 규칙이 없다. `Invoice.subscription.subscriptionId`
 * 를 넘긴다.
 *
 * ⚠️ 전환을 거친 구독을 청약철회 기간 이내에 이 API 로 산정하면 400 이다 — 되감기 소관이다.
 */
export const getCancellationRefundQuote = async (
  subscriptionId: number,
  referenceDate?: string,
): Promise<CancellationRefundQuote> => {
  const { data } = await apiClient.get(`/v1/admin/subscriptions/${subscriptionId}/cancellation-refund`, {
    params: referenceDate ? { referenceDate } : undefined,
  })
  return data
}

/** 되감기 잔류형 — 갈아탄 것만 무르고 구독은 계속한다. 취소할 결제 건이 하나뿐이다 */
export const getRollbackStayQuote = async (
  subscriptionId: number,
  referenceDate?: string,
): Promise<RollbackStayQuote> => {
  const { data } = await apiClient.get(`/v1/admin/subscriptions/${subscriptionId}/rollback-stay`, {
    params: referenceDate ? { referenceDate } : undefined,
  })
  return data
}

/**
 * 되감기 이탈형 — 무르고 그만둔다. 전환 결제 건·직전 약정 결제 건 2건으로 분해해서 낸다.
 *
 * 총액만으로는 실행할 수 없다 — 결제대행사가 결제 건 금액을 넘는 취소를 받지 않는다.
 */
export const getRollbackSettlementQuote = async (
  subscriptionId: number,
  referenceDate?: string,
): Promise<RollbackSettlementQuote> => {
  const { data } = await apiClient.get(`/v1/admin/subscriptions/${subscriptionId}/rollback-settlement`, {
    params: referenceDate ? { referenceDate } : undefined,
  })
  return data
}

/**
 * 크레딧 팩 결제 건 환불 산정 (aivatar #339 T-E4 · #537, 전달본 P2).
 *
 * **결제 건 id 로 지목한다** — 청구서 id 가 아니다(D-121). 다른 산정 3종처럼 400 이 정상 응답이라
 * `quoteOrReason` 으로 감싼다. 소멸한 결제 건·잔량을 믿을 수 없는 워크스페이스가 여기서 끊긴다.
 */
export const getCreditPackRefundQuote = async (
  paymentOrderId: number,
  referenceDate?: string,
): Promise<CreditPackRefundQuote> => {
  const { data } = await apiClient.get(`/v1/admin/payment-orders/${paymentOrderId}/credit-pack-refund`, {
    params: referenceDate ? { referenceDate } : undefined,
  })
  return data
}

/* ────────────────────────────── 조회 ────────────────────────────── */

/** 청구서 환불 현황 — 결제액·환불 합계(처리 완료)·접수 대기 금액·파생 상태·환불 행 목록 */
export const getInvoiceRefundView = async (invoiceId: number): Promise<InvoiceRefundView> => {
  const { data } = await apiClient.get(`/v1/admin/invoices/${invoiceId}/refunds`)
  return data
}

/**
 * 크레딧 팩 결제 건 환불 현황 — 청구서 현황의 단건판(D-121). 되감기 칸이 없다.
 *
 * 🔴 청구서 id 를 넘기지 말 것. 청구서 23 과 결제 건 23 이 함께 있을 수 있어 남의 현황을 읽는다.
 */
export const getPaymentOrderRefundView = async (paymentOrderId: number): Promise<PaymentOrderRefundView> => {
  const { data } = await apiClient.get(`/v1/admin/payment-orders/${paymentOrderId}/refunds`)
  return data
}

/**
 * 워크스페이스 환불대장.
 *
 * 정렬(접수·보류 먼저 → 최신순)·경과일·사건 묶음 위치·불일치 판정을 **서버가 해서 내린다.**
 * 화면에서 다시 정렬하지 않는다 — 규칙이 두 곳에 생긴다.
 *
 * 필터·페이지네이션 파라미터는 없다. 전체를 받아 클라이언트에서 거른다.
 */
export const getRefundLedger = async (workspaceId: number): Promise<RefundLedgerRow[]> => {
  const { data } = await apiClient.get(`/v1/admin/workspaces/${workspaceId}/refund-ledger`)
  return data ?? []
}

/** 같은 `refundGroupNo` 로 묶인 행들. 되감기 분해 내역·동반 환불 확인용 */
export const getRefundGroup = async (refundGroupNo: string): Promise<InvoiceRefund[]> => {
  const { data } = await apiClient.get('/v1/admin/invoice-refunds', { params: { refundGroupNo } })
  return data ?? []
}

/* ────────────────────────────── 접수·기록 ────────────────────────────── */

/**
 * 환불 1건 기록.
 *
 * `status: 'QUOTED'` = 접수 행. 산정 미리보기의 `amount`·`penalty`·`calculation` 을
 * **가공 없이 그대로** 넘긴다 — 서버가 산정을 다시 하지 않으므로 여기서 손대면 근거가 깨진다
 * (aivatar #488 (b)). 서버가 이 상태를 정식으로 받으며(`validateAgainstStatus`), 접수 행에
 * 처리 결과 4값을 함께 넣으면 400 이다 — 아직 결제대행사 취소를 실행하지 않았기 때문이다.
 *
 * `status` 생략 = 처리 완료 행. 단건 크레딧·회사 귀책 같은 수동 기록 경로다. 후속 조치가 붙는
 * 사유를 이 경로로 넣으면 서버가 `FAILED` 로 표시해 재시도 대상으로 남긴다.
 */
export const recordRefund = async (
  invoiceId: number,
  body: RecordRefundRequest,
): Promise<RecordRefundResponse> => {
  const { data } = await apiClient.post(`/v1/admin/invoices/${invoiceId}/refunds`, body)
  return data
}

/**
 * 크레딧 팩 결제 건 환불 1건 기록 — 요청 모양·상태 규칙은 `recordRefund` 와 같다.
 *
 * 사유는 `PAYMENT_ORDER_REASONS` 만 받는다(구독 사유는 400). 후속 조치는 사유와 무관하게 **남은
 * 크레딧 소멸**이다(D-124). 처리 완료로 바로 기록하면 그 결제 건에 남은 크레딧에 따라 "실행 안 함"
 * (남은 것 없음) 또는 "실패"(남음 · 모름 → 재시도가 소멸을 실행)로 남는다(aivatar #540).
 */
export const recordPaymentOrderRefund = async (
  paymentOrderId: number,
  body: RecordRefundRequest,
): Promise<RecordRefundResponse> => {
  const { data } = await apiClient.post(`/v1/admin/payment-orders/${paymentOrderId}/refunds`, body)
  return data
}

/**
 * 되감기 환불 접수 — **서버가 산정하고 행을 만든다.** 금액을 담당자가 넣지 않는다.
 *
 * 이탈형(`exit: true`)은 전환 결제 건·직전 약정 결제 건 2행이 같은 사건 번호로 만들어진다.
 * 둘 다 처리 완료로 확정돼야 후속 조치가 발화한다.
 */
export const receiveRollbackRefund = async (
  body: ReceiveRollbackRefundRequest,
): Promise<ReceiveRollbackRefundResponse> => {
  const { data } = await apiClient.post('/v1/admin/refunds/rollback', body)
  return data
}

/* ────────────────────────────── 확정·상태 전이 ────────────────────────────── */

/**
 * 처리 완료 확정.
 *
 * 🔴 **후속 조치의 트리거다.** 구독 즉시 비활성 전환, 직전 약정 복원이 여기서 실행되고
 * 되돌릴 수 없다. 화면은 `dryRun: true` 결과를 보여준 뒤에만 확정 버튼을 연다.
 *
 * `dryRun` 은 미리보기이면서 **입력 검증기**다 — 서버 `preview()` 가 확정과 똑같은
 * `validateCompletion()` 을 통과시킨다. 미리보기가 200 이면 같은 본문의 확정도 통과한다.
 *
 * 접수(`QUOTED`)뿐 아니라 **보류(`ON_HOLD`)에서도 확정할 수 있다**(`InvoiceRefund.complete`).
 * 보류를 풀고 다시 접수하는 단계는 없다.
 *
 * `refundedAt`(처리일)은 메모가 아니라 **비활성 진입일**이다. 미래도, 붙어 있는 청구서의
 * 결제 시각보다 이른 값도 400. BE 는 `LocalDateTime` 을 받으므로 날짜만 보내면 파싱에서 끊긴다.
 */
export const completeRefund = async (
  refundId: number,
  body: CompleteRefundRequest,
): Promise<CompleteRefundResponse> => {
  const { data } = await apiClient.post(`/v1/admin/invoice-refunds/${refundId}/complete`, body)
  return data
}

/** 보류 — 고객 확인을 기다린다. 접수 상태에서만 가능하다 */
export const holdRefund = async (refundId: number, memo?: string): Promise<InvoiceRefund> => {
  const { data } = await apiClient.post(`/v1/admin/invoice-refunds/${refundId}/hold`, { memo })
  return data
}

/**
 * 철회 — 재산정의 유일한 경로다. 접수 행의 산정값은 고칠 수 없으므로(고객에게 안내·동의받은
 * 금액이다) 기준일이 지나 다시 계산해야 하면 원 행을 철회하고 새로 접수한다.
 *
 * 처리 완료된 행은 철회할 수 없다 — 돈이 이미 나갔다. 그 경우의 정정은 새 기록이다.
 */
export const withdrawRefund = async (refundId: number, memo?: string): Promise<InvoiceRefund> => {
  const { data } = await apiClient.post(`/v1/admin/invoice-refunds/${refundId}/withdraw`, { memo })
  return data
}

/**
 * 후속 조치 재시도 — 실패했거나 짝을 기다리던 건만 가능하다.
 *
 * 이미 실행된 것을 다시 돌리면 구독을 두 번 끄거나 복원을 두 번 한다. 서버가 막지만
 * 화면도 `followUpStatus` 가 `FAILED`·`PENDING_GROUP` 일 때만 버튼을 연다.
 */
export const retryRefundFollowUp = async (refundId: number): Promise<CompleteRefundResponse> => {
  const { data } = await apiClient.post(`/v1/admin/invoice-refunds/${refundId}/follow-up/retry`)
  return data
}

/* ────────────────────────────── 쿼리 키 ────────────────────────────── */

/**
 * 산정 3종은 여기 없다. 담당자가 모달을 열 때 한 번 부르는 값이라 캐시에 얹으면 화면을 다시
 * 열어도 낡은 기준일 산정이 그대로 보인다 — 호출부에서 직접 부른다.
 */
export const refundQueries = createQueryKeys('refund', {
  ledger: (workspaceId: number) => ({
    queryKey: [workspaceId],
    queryFn: () => getRefundLedger(workspaceId),
  }),
  invoiceView: (invoiceId: number) => ({
    queryKey: [invoiceId],
    queryFn: () => getInvoiceRefundView(invoiceId),
  }),
  paymentOrderView: (paymentOrderId: number) => ({
    queryKey: [paymentOrderId],
    queryFn: () => getPaymentOrderRefundView(paymentOrderId),
  }),
  group: (refundGroupNo: string) => ({
    queryKey: [refundGroupNo],
    queryFn: () => getRefundGroup(refundGroupNo),
  }),
})
